import { useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Loader2, ShieldAlert, UserRound, ShieldCheck } from "lucide-react";
import { ppost } from "./portalApi";

const inputCls = "w-full rounded-2xl border border-sand-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-redline-500/30";

export default function PortalAccount() {
  const { me } = useOutletContext();
  const [params] = useSearchParams();
  const [hasPw, setHasPw] = useState(!!me?.has_password);
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const setup = params.get("setup") === "1" && !hasPw;

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { setErr("Use at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Those passwords don't match."); return; }
    setBusy(true); setErr("");
    try {
      await ppost("/resident/auth/password/change", { current_password: current, new_password: pw });
      setHasPw(true); setCurrent(""); setPw(""); setPw2("");
      toast.success(hasPw ? "Password changed." : "Password set — sign in with it from now on.");
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : d?.message || "Couldn't save that password.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="portal-account-page">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ink">Your account</h1>
        <p className="text-sm text-muted-foreground mt-1">How you sign in to the {me?.community?.name || "community"} portal.</p>
      </div>

      <div className="rounded-2xl border border-sand-200 bg-white p-5 flex items-center gap-4" data-testid="portal-account-identity">
        <div className="h-11 w-11 rounded-full bg-redline-500 text-white flex items-center justify-center font-bold shrink-0"><UserRound className="h-5 w-5" /></div>
        <div className="min-w-0 text-sm">
          <div className="font-heading font-bold text-ink truncate">{me?.person?.name}</div>
          <div className="text-muted-foreground truncate">{[me?.person?.phone, me?.person?.email].filter(Boolean).join(" · ") || "No contact on file"}</div>
          {me?.person?.account_number && <div className="text-xs text-muted-foreground mt-0.5">Account {me.person.account_number}</div>}
        </div>
      </div>

      <form onSubmit={submit} className={`rounded-2xl border bg-white p-5 ${setup ? "border-amber-300" : "border-sand-200"}`} data-testid="portal-account-password">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="h-5 w-5 text-redline-500 shrink-0" strokeWidth={1.75} />
          <h2 className="font-heading text-lg font-bold text-ink">{hasPw ? "Password" : "Set a password"}</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          {hasPw ? "You sign in with your mobile or email and this password." : "Set one now and you'll sign in with your mobile or email and this password from any device — no codes to wait for."}
        </p>
        <div className="max-w-sm space-y-3">
          {hasPw && <input data-testid="portal-account-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Current password" className={inputCls} />}
          <input data-testid="portal-account-new" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (8+ characters)" className={inputCls} />
          <input data-testid="portal-account-confirm" type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Type it again" className={inputCls} />
          {err && <p className="text-sm text-danger-700 flex items-start gap-1.5" data-testid="portal-account-error"><ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" /> {err}</p>}
          <button data-testid="portal-account-submit" type="submit" disabled={busy}
            className="w-full rounded-full bg-ink text-white font-bold py-3 text-sm hover:bg-ink/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : (hasPw ? "Change password" : "Save password")}
          </button>
        </div>
      </form>

      <p className="text-xs text-muted-foreground flex items-start gap-1.5"><ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" /> You stay signed in on this device until you sign out. Forgot your password? On the sign-in page, choose "Forgot password" and we'll send a code to the contact on file.</p>
    </div>
  );
}
